import { type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import type { ObjectId } from '@/types';
import { useSelectionStore } from '@/store/selectionStore';
import { useObject } from '@/hooks/useObject';
import { useObjectWorldMatrix } from '@/hooks/useObjectWorldMatrix';
import { useCanvasInteractionController } from '@/features/canvas/CanvasInteractionContext';
import { useEngine } from '@/app/EngineContext';
import { RESIZE_HANDLES, type ResizeHandle } from '@/core/interaction/manipulation';
import { objectType } from '@/core/scene/objectTypes';
import styles from './SelectionHandles.module.css';

/**
 * Resize and rotate handles for a single selected, unlocked object. The frame is
 * positioned with the object's world matrix so handles follow rotation and scale.
 * Multi-selection shows no handles: group manipulation is a Phase 4 concern.
 */
export const SelectionHandles = () => {
  const ids = useSelectionStore((s) => s.selection.objectIds);
  const only = ids.length === 1 ? ids[0] : undefined;
  return only ? <HandlesFor id={only} /> : null;
};

const HandlesFor = ({ id }: { id: ObjectId }) => {
  const obj = useObject(id);
  const matrix = useObjectWorldMatrix(id);
  const { beginHandleDrag } = useCanvasInteractionController();
  const { scene } = useEngine();
  if (!obj || !matrix || obj.locked || !scene.isInteractive(id)) return null;
  const def = objectType(obj.kind);

  const start = (handle: ResizeHandle | 'rotate') => (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    beginHandleDrag(e, id, handle);
  };

  const frame: CSSProperties = {
    transform: matrix,
    width: obj.size.width,
    height: obj.size.height,
  };

  return (
    <div className={styles.frame} style={frame} data-testid="selection-handles" aria-hidden="true">
      {def.resizable &&
        RESIZE_HANDLES.map((h) => (
          <span
            key={h}
            className={styles.handle}
            data-handle={h}
            data-testid={`handle-${h}`}
            onPointerDown={start(h)}
          />
        ))}
      {def.rotatable && (
        <>
          <span className={styles.rotateStem} />
          <span className={styles.rotate} data-testid="handle-rotate" onPointerDown={start('rotate')} />
        </>
      )}
    </div>
  );
};
